/**
 * OFFICIAL SINGLE SOURCE - Product Display Types
 * 
 * Unified type system that eliminates mismatches between:
 * - UnifiedProductsPage → EnhancedShopifyProductCard → ShopifyProductCard → QuickViewModal
 * 
 * Consolidates:
 * - QuickViewProduct (legacy)
 * - ShopifyProduct (base)  
 * - Enhanced product wrappers
 * - Display-specific metadata
 */

import { ShopifyProduct, ShopifyVariant } from '@/hooks/useShopify';

/**
 * OFFICIAL SINGLE SOURCE - Unified display product interface
 * Used by: All product cards, QuickView, product pages
 */
export interface DisplayProduct extends ShopifyProduct {
  // Display-specific metadata
  _displayMeta: {
    matchedVariant?: ShopifyVariant | null;
    selectionReason?: 'user' | 'inStock' | 'matched' | 'priceSort' | 'available' | 'fallback';
    imageIndex?: number;
    imageMatchMethod?: 'exact_id' | 'url_match' | 'color_alt' | 'variant_image' | 'fallback';
    lastUpdated: number; // timestamp for cache invalidation
  };

  // Computed display properties
  displayPrice: string; // Formatted price string
  displayCompareAtPrice?: string; // Formatted compare price
  displayImageUrl: string; // Current display image URL
  isInStock: boolean; // Computed availability
  stockLevel: 'high' | 'medium' | 'low' | 'out'; // Stock indicator
}

/**
 * Product card display configuration
 */
export interface ProductCardConfig {
  size: 'large' | 'small';
  columnCount?: 2 | 3 | 4;
  showPurchaseControls: boolean;
  showQuickView: boolean;
  showFavorites: boolean;
  hideOutOfStock?: boolean;
}

/**
 * QuickView modal configuration  
 */
export interface QuickViewConfig {
  showVariantSelector: boolean;
  showQuantitySelector: boolean;
  showTrustIndicators: boolean;
  showReviews: boolean;
  maxQuantity?: number;
}

/**
 * Filter criteria for product selection
 */
export interface ProductFilterCriteria {
  searchQuery?: string;
  category?: string;
  priceRange?: [number, number];
  sizes?: string[];
  colors?: string[];
  hideOutOfStock?: boolean;
  sortBy?: 'title' | 'price_asc' | 'price_desc' | 'newest' | 'oldest' | 'updated';
}

/**
 * Product display context for component communication
 */
export interface ProductDisplayContext {
  filters: ProductFilterCriteria;
  viewConfig: ProductCardConfig;
  quickViewConfig: QuickViewConfig;
  currentPage?: number;
  totalResults?: number;
}

/**
 * Type guards for product validation
 */
export const isDisplayProduct = (product: any): product is DisplayProduct => {
  return product && 
         typeof product === 'object' && 
         product.id && 
         product.title &&
         product._displayMeta &&
         typeof product._displayMeta.lastUpdated === 'number';
};

export const isShopifyProduct = (product: any): product is ShopifyProduct => {
  return product && 
         typeof product === 'object' && 
         product.id && 
         product.title &&
         product.variants &&
         Array.isArray(product.variants);
};

/**
 * Convert ShopifyProduct to DisplayProduct
 */
export const createDisplayProduct = (
  product: ShopifyProduct,
  matchedVariant?: ShopifyVariant | null,
  options?: Partial<ProductDisplayContext>
): DisplayProduct => {
  const selectedVariant = matchedVariant || product.variants[0];
  
  // Format prices consistently
  const formatPrice = (amount: string | number) => {
    const price = typeof amount === 'string' ? parseFloat(amount) : amount;
    return price.toFixed(2);
  };

  const displayPrice = selectedVariant ? 
    formatPrice(selectedVariant.price.amount) : 
    formatPrice(product.priceRange?.minVariantPrice?.amount || '0');

  const displayCompareAtPrice = selectedVariant?.compareAtPrice?.amount ?
    formatPrice(selectedVariant.compareAtPrice.amount) : undefined;

  const displayImageUrl = selectedVariant?.image?.url || 
                         product.images?.[0]?.url || 
                         '';

  // Determine stock level
  const getStockLevel = (variant: ShopifyVariant): 'high' | 'medium' | 'low' | 'out' => {
    if (!variant.availableForSale) return 'out';
    
    const qty = variant.quantityAvailable;
    if (qty === null) return 'high'; // Unlimited stock
    if (qty > 10) return 'high';
    if (qty > 3) return 'medium';
    if (qty > 0) return 'low';
    return 'out';
  };

  return {
    ...product,
    _displayMeta: {
      matchedVariant,
      selectionReason: 'fallback',
      imageIndex: 0,
      imageMatchMethod: 'fallback',
      lastUpdated: Date.now()
    },
    displayPrice,
    displayCompareAtPrice,
    displayImageUrl,
    isInStock: selectedVariant?.availableForSale && 
               (selectedVariant.quantityAvailable === null || selectedVariant.quantityAvailable > 0),
    stockLevel: selectedVariant ? getStockLevel(selectedVariant) : 'out'
  };
};

/**
 * Update display product with new variant selection
 */
export const updateDisplayProductVariant = (
  displayProduct: DisplayProduct,
  newVariant: ShopifyVariant,
  imageIndex?: number
): DisplayProduct => {
  const formatPrice = (amount: string | number) => {
    const price = typeof amount === 'string' ? parseFloat(amount) : amount;
    return price.toFixed(2);
  };

  return {
    ...displayProduct,
    _displayMeta: {
      ...displayProduct._displayMeta,
      matchedVariant: newVariant,
      selectionReason: 'user',
      imageIndex: imageIndex || displayProduct._displayMeta.imageIndex || 0,
      lastUpdated: Date.now()
    },
    displayPrice: formatPrice(newVariant.price.amount),
    displayCompareAtPrice: newVariant.compareAtPrice?.amount ? 
      formatPrice(newVariant.compareAtPrice.amount) : undefined,
    displayImageUrl: newVariant.image?.url || displayProduct.displayImageUrl,
    isInStock: newVariant.availableForSale && 
               (newVariant.quantityAvailable === null || newVariant.quantityAvailable > 0),
    stockLevel: (() => {
      if (!newVariant.availableForSale) return 'out';
      const qty = newVariant.quantityAvailable;
      if (qty === null) return 'high';
      if (qty > 10) return 'high';
      if (qty > 3) return 'medium';
      if (qty > 0) return 'low';
      return 'out';
    })()
  };
};

/**
 * Batch convert ShopifyProducts to DisplayProducts
 */
export const createDisplayProducts = (
  products: ShopifyProduct[],
  context?: ProductDisplayContext
): DisplayProduct[] => {
  return products.map(product => createDisplayProduct(product, null, context));
};